/**
 * The figure component.
 *
 * Renders a validated `PlotSpec` as an interactive SVG: sliders drive the
 * expressions, a crosshair reads values off the curves, and a numbered caption
 * sits underneath the way it would in a textbook.
 */

import { useCallback, useMemo, useRef, useState } from "react";
import type { PlotSpec } from "../../shared/plot-spec.ts";
import { useTheme } from "../theme.tsx";
import { computeLayout, initialParams } from "./geometry.ts";
import { buildPlotModel, type ResolvedSeries } from "./model.ts";
import { createScale, type Scale } from "./scales.ts";
import { Axes } from "./Axes.tsx";
import { SeriesLayer, layerDepth } from "./SeriesLayer.tsx";
import { PlotControls } from "./PlotControls.tsx";
import { CHROME } from "./palette.ts";
import { formatValue, stripUnits } from "./format.ts";
import type { Point } from "./compile.ts";
import { MathText } from "../markdown/MathText.tsx";
import { Maximize2 } from "lucide-react";
import { Button } from "../components/ui/button.tsx";
import { FigureOverlay } from "./FigureOverlay.tsx";

interface PlotProps {
  readonly spec: PlotSpec;
  /** Figure number within the answer, for the caption. */
  readonly index?: number;
  /** False for the copy already inside the overlay, so it cannot nest. */
  readonly expandable?: boolean;
}

export function Plot({ spec, index, expandable = true }: PlotProps) {
  const { mode } = useTheme();
  const [params, setParams] = useState(() => initialParams(spec));
  // Lifted out of the canvas so the HTML overlay can render the readout.
  const [hover, setHover] = useState<HoverState | null>(null);
  /**
   * A dense vector field or a heatmap is unreadable at the width of a text
   * column. Expanding drops the measure and gives it the window.
   */
  const [expanded, setExpanded] = useState(false);
  const layout = useMemo(() => computeLayout(spec), [spec]);

  const model = useMemo(
    () => buildPlotModel({ spec, params, mode, aspect: layout.aspect }),
    [spec, params, mode, layout.aspect],
  );

  const xScale = useMemo(
    () =>
      createScale(
        model.xDomain,
        [layout.left, layout.left + layout.innerWidth],
        spec.xScale,
      ),
    [model.xDomain, layout, spec.xScale],
  );

  const yScale = useMemo(
    () =>
      // Range inverted: SVG y grows downward, physics y grows upward.
      createScale(
        model.yDomain,
        [layout.top + layout.innerHeight, layout.top],
        spec.yScale,
      ),
    [model.yDomain, layout, spec.yScale],
  );

  const setParam = useCallback((name: string, value: number) => {
    setParams((current) => ({ ...current, [name]: value }));
  }, []);

  const reset = useCallback(() => setParams(initialParams(spec)), [spec]);

  const ordered = useMemo(
    () => [...model.series].sort((a, b) => layerDepth(a) - layerDepth(b)),
    [model.series],
  );

  const failures = model.series.filter((s) => s.body.kind === "failed");
  const chrome = CHROME[mode];
  const showLegend = spec.legend && model.series.length > 1;

  return (
    <figure data-plot="figure" className="my-8">
      {spec.title && (
        <MathText
          className="mb-2 block font-serif text-base text-foreground"
          children={spec.title}
        />
      )}

      {/*
        The axis labels are HTML in a grid around the SVG rather than `<text>`
        inside it, which is the only way they can carry mathematics.
      */}
      <div className="grid grid-cols-[auto_minmax(0,1fr)] items-center gap-x-1">
        {spec.yLabel ? (
          <MathText
            className="[writing-mode:vertical-rl] rotate-180 justify-self-center font-serif text-sm text-foreground"
            children={spec.yLabel}
          />
        ) : (
          <span />
        )}

        {/* `relative` anchors the HTML overlay that carries typeset labels. */}
        <div className="group/figure relative overflow-hidden rounded-lg border bg-[var(--surface)] shadow-[var(--shadow)]">
          {expandable && (
            <Button
              variant="ghost"
              size="icon-sm"
              onClick={() => setExpanded(true)}
              aria-label="Expand figure"
              className="no-print absolute top-1.5 right-1.5 z-10 opacity-0 transition-opacity group-hover/figure:opacity-100 focus-visible:opacity-100"
            >
              <Maximize2 className="size-3.5" />
            </Button>
          )}
          <PlotCanvas
            spec={spec}
            model={ordered}
            xScale={xScale}
            yScale={yScale}
            layout={layout}
            mode={mode}
            hover={hover}
            onHover={setHover}
          />
          <PlotOverlay
            spec={spec}
            hover={hover}
            xScale={xScale}
            yScale={yScale}
            layout={layout}
          />
        </div>

        {spec.xLabel && (
          <MathText
            className="col-start-2 mt-1 justify-self-center font-serif text-sm text-foreground"
            children={spec.xLabel}
          />
        )}
      </div>

      {showLegend && <Legend series={model.series} />}

      {spec.params.length > 0 && (
        <PlotControls
          params={spec.params}
          values={params}
          onChange={setParam}
          onReset={reset}
        />
      )}

      {failures.length > 0 && (
        <div
          role="status"
          className="my-4 rounded-r-md border-l-[3px] border-l-warning bg-muted px-4 py-3 font-sans text-sm"
        >
          {failures.map((series) => (
            <p key={series.key} className="mb-1 last:mb-0">
              <strong className="font-semibold">
                <MathText children={series.label} />
              </strong>{" "}
              could not be drawn —{" "}
              {series.body.kind === "failed" ? series.body.message : ""}
            </p>
          ))}
        </div>
      )}

      {(spec.caption || index !== undefined) && (
        <figcaption className="mt-3 text-[0.94em] leading-relaxed text-secondary-foreground">
          {index !== undefined && (
            <span className="font-bold text-foreground">Figure {index}.</span>
          )}{" "}
          {spec.caption && <MathText children={spec.caption} />}
        </figcaption>
      )}

      <span className="sr-only" style={{ color: chrome.ink }}>
        {describeForScreenReader(spec, model.series)}
      </span>

      {expandable && (
        <FigureOverlay
          open={expanded}
          onClose={() => setExpanded(false)}
          title={spec.title}
        >
          {/* A fresh instance, so the expanded copy keeps its own slider state
              and the inline one is not disturbed by exploring it. */}
          <Plot spec={spec} index={index} expandable={false} />
        </FigureOverlay>
      )}
    </figure>
  );
}

/* -------------------------------------------------------------------------- */
/* Canvas + hover                                                              */
/* -------------------------------------------------------------------------- */

interface CanvasProps {
  readonly spec: PlotSpec;
  readonly model: readonly ResolvedSeries[];
  readonly xScale: Scale;
  readonly yScale: Scale;
  readonly layout: ReturnType<typeof computeLayout>;
  readonly mode: "paper" | "night";
  readonly hover: HoverState | null;
  readonly onHover: (state: HoverState | null) => void;
}

interface HoverState {
  readonly x: number;
  readonly dataX: number;
  readonly readings: readonly { label: string; color: string; y: number }[];
}

function PlotCanvas({
  spec,
  model,
  xScale,
  yScale,
  layout,
  mode,
  hover,
  onHover,
}: CanvasProps) {
  const svgRef = useRef<SVGSVGElement>(null);
  const chrome = CHROME[mode];
  const setHover = onHover;

  const onMove = useCallback(
    (event: React.PointerEvent<SVGSVGElement>) => {
      const svg = svgRef.current;
      if (!svg) return;

      // Map client pixels into viewBox units — the SVG is CSS-scaled.
      const rect = svg.getBoundingClientRect();
      if (rect.width === 0) return;
      const svgX = ((event.clientX - rect.left) / rect.width) * layout.width;

      if (svgX < layout.left || svgX > layout.left + layout.innerWidth) {
        setHover(null);
        return;
      }

      const dataX = xScale.invert(svgX);
      const readings = model
        .map((series) => readAt(series, dataX))
        .filter((reading): reading is NonNullable<typeof reading> => reading !== null);

      setHover(readings.length === 0 ? null : { x: svgX, dataX, readings });
    },
    [layout, model, xScale],
  );

  const clear = useCallback(() => setHover(null), []);

  return (
    <svg
      ref={svgRef}
      viewBox={`0 0 ${layout.width} ${layout.height}`}
      className="plot-svg"
      role="img"
      onPointerMove={onMove}
      onPointerLeave={clear}
    >
      <rect
        x={layout.left}
        y={layout.top}
        width={layout.innerWidth}
        height={layout.innerHeight}
        fill={chrome.surface}
      />

      <Axes
        layout={layout}
        xScale={xScale}
        yScale={yScale}
        mode={mode}
        showGrid={spec.grid}
      />

      {/* Clip so a steep curve can't scribble over the axis labels. */}
      <defs>
        <clipPath id={`clip-${layout.innerWidth}-${layout.innerHeight}`}>
          <rect
            x={layout.left}
            y={layout.top}
            width={layout.innerWidth}
            height={layout.innerHeight}
          />
        </clipPath>
      </defs>

      <g clipPath={`url(#clip-${layout.innerWidth}-${layout.innerHeight})`}>
        {model.map((series) => (
          <SeriesLayer
            key={series.key}
            series={series}
            xScale={xScale}
            yScale={yScale}
            layout={layout}
            mode={mode}
          />
        ))}

        {/* Only the marker dot; its label is typeset in the HTML overlay. */}
        {spec.annotations.map((annotation, index) => (
          <circle
            key={`ann-${index}`}
            cx={xScale.to(annotation.x)}
            cy={yScale.to(annotation.y)}
            r={3.5}
            fill={chrome.ink}
          />
        ))}

        {hover && (
          <g pointerEvents="none">
            <line
              x1={hover.x}
              x2={hover.x}
              y1={layout.top}
              y2={layout.top + layout.innerHeight}
              stroke={chrome.muted}
              strokeWidth={1}
              strokeDasharray="4 4"
            />
            {hover.readings.map((reading) => (
              <circle
                key={reading.label}
                cx={hover.x}
                cy={yScale.to(reading.y)}
                r={4}
                fill={reading.color}
                stroke={chrome.surface}
                strokeWidth={2}
              />
            ))}
          </g>
        )}
      </g>

    </svg>
  );
}

/**
 * Annotations and the hover readout, as HTML layered over the figure.
 *
 * They were SVG `<text>` and so printed `$x(0)=x_0$` literally. Positioning is
 * in percentages of the viewBox, which is exact because the SVG preserves its
 * aspect ratio and fills the container.
 */
function PlotOverlay({
  spec,
  hover,
  xScale,
  yScale,
  layout,
}: {
  spec: PlotSpec;
  hover: HoverState | null;
  xScale: Scale;
  yScale: Scale;
  layout: ReturnType<typeof computeLayout>;
}) {
  const percent = (value: number, total: number): string => `${(value / total) * 100}%`;

  return (
    <div className="pointer-events-none absolute inset-0">
      {spec.annotations.map((annotation, index) => {
        const x = xScale.to(annotation.x);
        const y = yScale.to(annotation.y);
        if (!withinPlot(x, y, layout)) return null;

        return (
          <MathText
            key={`ann-${index}`}
            className="absolute -translate-y-full font-serif text-[0.8rem] whitespace-nowrap text-foreground"
            style={{
              left: percent(x + 8, layout.width),
              top: percent(y - 6, layout.height),
            }}
            children={annotation.text}
          />
        );
      })}

      {hover && (
        <div
          className="absolute w-44 rounded-md border bg-[var(--surface)]/95 px-2.5 py-2 font-sans text-xs shadow-sm"
          style={{
            // Flip to the left of the crosshair near the right edge.
            left: percent(
              hover.x + 176 + 12 > layout.left + layout.innerWidth
                ? hover.x - 176 - 12
                : hover.x + 12,
              layout.width,
            ),
            top: percent(layout.top + 10, layout.height),
          }}
        >
          <p className="mb-1 tabular-nums text-muted-foreground">
            <MathText children={spec.xLabel ? stripUnits(spec.xLabel) : "$x$"} />{" "}
            = {formatValue(hover.dataX)}
          </p>
          {hover.readings.map((reading) => (
            <p
              key={reading.label}
              className="flex items-center gap-1.5 tabular-nums text-foreground"
            >
              <span
                aria-hidden="true"
                className="size-2 shrink-0 rounded-full"
                style={{ background: reading.color }}
              />
              {/* Trimmed by CSS, never by character count. A label like
                  "circle: $x^2+y^2=1$" cut to 14 characters loses its closing
                  delimiter, and MathText then has no maths to typeset — which
                  is how the reader ends up looking at raw TeX. */}
              <MathText
                className="min-w-0 truncate"
                children={reading.label}
              />
              <span className="ml-auto">{formatValue(reading.y)}</span>
            </p>
          ))}
        </div>
      )}
    </div>
  );
}

const withinPlot = (
  x: number,
  y: number,
  layout: ReturnType<typeof computeLayout>,
): boolean =>
  x >= layout.left &&
  x <= layout.left + layout.innerWidth &&
  y >= layout.top &&
  y <= layout.top + layout.innerHeight;


/** Nearest sample to `dataX` on a curve, if one is close enough to mean anything. */
function readAt(
  series: ResolvedSeries,
  dataX: number,
): { label: string; color: string; y: number } | null {
  if (series.body.kind !== "curve") return null;

  let best: Point | null = null;
  let bestDistance = Infinity;

  for (const segment of series.body.segments) {
    for (const point of segment) {
      const distance = Math.abs(point.x - dataX);
      if (distance < bestDistance) {
        bestDistance = distance;
        best = point;
      }
    }
  }

  if (!best) return null;
  return { label: series.label, color: series.color, y: best.y };
}

/* -------------------------------------------------------------------------- */
/* Legend                                                                      */
/* -------------------------------------------------------------------------- */

function Legend({ series }: { series: readonly ResolvedSeries[] }) {
  return (
    <ul className="mt-3 flex flex-wrap gap-x-4 gap-y-2 font-sans text-sm text-secondary-foreground">
      {series.map((entry) => (
        <li key={entry.key} className="flex items-center gap-2">
          <span
            className="h-[3px] w-3 rounded-sm"
            style={{ background: entry.color }}
            aria-hidden="true"
          />
          <MathText className="font-serif" children={entry.label} />
        </li>
      ))}
    </ul>
  );
}

/* -------------------------------------------------------------------------- */
/* Description                                                                 */
/* -------------------------------------------------------------------------- */

function describeForScreenReader(
  spec: PlotSpec,
  series: readonly ResolvedSeries[],
): string {
  const what = series.map((s) => s.label).join(", ");
  return [
    spec.title ?? "Figure",
    `plotting ${what}`,
    `over ${spec.xLabel ?? "x"} from ${formatValue(spec.xRange[0])} to ${formatValue(spec.xRange[1])}.`,
    spec.caption ?? "",
  ].join(" ");
}
